// People (AdminPeople.dc.html): everyone with a login, searched, filtered and paged on the server
// (hub.staff_people, docs/data-model.md 10.4.2), with Add a person, Grant in bulk and Export CSV for owner
// and admin. Support sees the list; a host sees only their products' people. Products not moved into
// the hub (POYT and the Accelerator on live in phase 1) show only as ownership outside the hub,
// labelled, and never feed a grant. Joined is the purchase (the earliest access start), not the day a
// row was made, so imported buyers keep their real date. At phone width the table stacks (answer 5).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import {
  PageHeader, Button, Card, Chip, Chips, Dot, Flash, Loading, Problem, SearchBox, StatusPill, Table,
  downloadCsv, useAdmin, adminPath,
} from '../ui/index.js'
import { PEOPLE, STATUS_CHIPS, SPACE, BULK, byBoardOrder, freeChipName, lastSeen, listStatus, listTags, shortName, boardDay, batchDone, batchUndone } from '../people/words.js'
import { listPeople, loadCatalogue } from '../people/data.js'
import { CSV_PAGE } from '../people/columns.js'
import { AddPerson } from '../people/AddPerson.jsx'
import { BulkGrant } from '../people/BulkGrant.jsx'
import { OpenSpaceDialog, liveSpaceProducts } from '../people/OpenSpaceDialog.jsx'
import '../../styles/admin-people.css'

// The filters live in the address, so going back from a person's page keeps them
const KEYS = { search: 'q', status: 'status', product: 'product', free: 'free' }
function readFilters() {
  const q = new URLSearchParams(window.location.search)
  return { search: q.get('q') || '', status: q.get('status') || '', product: q.get('product') || '', free: q.get('free') || '' }
}
function writeFilters(f) {
  const q = new URLSearchParams()
  for (const [k, name] of Object.entries(KEYS)) if (f[k]) q.set(name, f[k])
  const next = `${window.location.pathname}${q.toString() ? `?${q}` : ''}`
  if (next !== window.location.pathname + window.location.search) window.history.replaceState(window.history.state, '', next)
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

export default function People({ role, scopes }) {
  const { api, products } = useAdmin()
  const oa = role === 'owner' || role === 'admin'
  const [filters, setFilters] = useState(readFilters)
  const search = useDebounced(filters.search, 300)
  const [list, setList] = useState({ status: 'loading', rows: [], total: 0, error: null })
  const [more, setMore] = useState(false)
  const [panel, setPanel] = useState(null) // 'add' | 'bulk'
  const [batch, setBatch] = useState(null) // { id, product, text, canUndo }
  const [exported, setExported] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [csvError, setCsvError] = useState(null)
  const [opening, setOpening] = useState(false)
  const [catalogue, setCatalogue] = useState(null)
  const seq = useRef(0)
  const apiRef = useRef(api)
  apiRef.current = api

  const productsById = useMemo(() => new Map(products.map(p => [p.id, p])), [products])
  // the product chips: the paid products (a host only theirs), and the 1-2-1 for owner and admin
  const productChips = useMemo(() => byBoardOrder(products.filter(p => p.access_kind === 'paid'
    && (role === 'host' ? scopes.includes(p.id) : p.type !== 'client_space' || oa))), [products, role, scopes, oa])
  // Used free: what the hub itself records (a scorecard opened, a guide downloaded, a challenge joined).
  // Free webinars' chip, "A free webinar", went with Zoom's registrations (Adam, 5 Oct, stage 6)
  const freeChips = useMemo(() => (role === 'host' ? [] : products.filter(p => p.access_kind === 'free' && p.state !== 'archived' && p.type !== 'webinar')), [products, role])

  const query = useMemo(() => ({ search, status: filters.status, product: filters.product, free: filters.free }), [search, filters.status, filters.product, filters.free])
  const load = useCallback(async () => {
    const n = ++seq.current
    setList(l => ({ ...l, status: l.rows.length ? 'reloading' : 'loading', error: null }))
    try {
      const rows = await listPeople(apiRef.current, query)
      if (n === seq.current) setList({ status: 'ready', rows, total: rows[0]?.total_count ?? 0, error: null })
    } catch (e) {
      if (n === seq.current) setList(l => ({ ...l, status: 'error', error: e }))
    }
  }, [query])
  useEffect(() => { writeFilters(filters) }, [filters])
  useEffect(() => { load() }, [load])

  // the catalogue with what a grant needs (moved in, how it releases) and the intakes, for the grant
  // boxes: the shell's own read is the Library's columns
  const [catalogueError, setCatalogueError] = useState(null)
  useEffect(() => {
    if (!oa) return undefined
    let alive = true
    loadCatalogue(apiRef.current).then(c => { if (alive) setCatalogue(c) }, e => { if (alive) setCatalogueError(e) })
    return () => { alive = false }
  }, [oa])

  async function showMore() {
    if (more) return
    const last = list.rows[list.rows.length - 1]
    setMore(true)
    try {
      const rows = await listPeople(apiRef.current, query, last)
      setList(l => ({ ...l, rows: [...l.rows, ...rows], total: rows[0]?.total_count ?? l.total }))
    } catch (e) {
      setList(l => ({ ...l, error: e }))
    } finally {
      setMore(false)
    }
  }

  async function exportCsv() {
    if (exporting) return
    setExporting(true)
    setCsvError(null)
    try {
      let rows = [], cursor = null
      for (;;) {
        const page = await listPeople(apiRef.current, query, cursor, CSV_PAGE)
        rows = rows.concat(page)
        if (page.length < CSV_PAGE) break
        cursor = page[page.length - 1]
      }
      downloadCsv(PEOPLE.csvName, [
        { key: 'full_name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { label: 'Products', value: r => listTags(r, productsById).map(t => t.label).join('; ') },
        { label: 'Status', value: r => listStatus(r, productsById) },
        { key: 'last_seen_at', label: 'Last seen', format: 'date' },
        { key: 'joined_at', label: 'Joined', format: 'date' },
      ], rows)
      setExported(true)
    } catch (e) {
      setCsvError(e)
    } finally {
      setExporting(false)
    }
  }

  const set = patch => setFilters(f => ({ ...f, ...patch }))

  function granted({ answer, product, emailEach }) {
    const c = answer.counts || {}
    setPanel(null)
    setBatch({
      id: answer.batch_id, product, granted: c.granted ?? (answer.granted || []).length, canUndo: true,
      text: batchDone({ product, granted: c.granted ?? (answer.granted || []).length, skipped: (c.has_it || 0) + (c.duplicate || 0), emailEach, emailed: answer.emailed || 0, failed: answer.email_failed || 0 }),
    })
    load()
  }
  const [undoing, setUndoing] = useState(false)
  async function undo() {
    if (!batch?.canUndo || undoing) return
    setUndoing(true)
    try {
      await api.undo_grant_batch(batch.id)
      setBatch({ ...batch, canUndo: false, text: batchUndone(batch.product, batch.granted) })
      load()
    } catch (e) {
      setBatch({ ...batch, error: e.message })
    } finally {
      setUndoing(false)
    }
  }

  const rows = list.rows
  const actions = oa ? (
    <>
      <Button aria-expanded={panel === 'bulk'} onClick={() => setPanel(p => (p === 'bulk' ? null : 'bulk'))}>{panel === 'bulk' ? PEOPLE.bulkClose : PEOPLE.bulk}</Button>
      <Button onClick={exportCsv} busy={exporting}>{exported ? PEOPLE.csvDone : PEOPLE.csv}</Button>
      <Button kind="ink" aria-expanded={panel === 'add'} onClick={() => setPanel(p => (p === 'add' ? null : 'add'))}>{panel === 'add' ? PEOPLE.addCancel : PEOPLE.add}</Button>
    </>
  ) : null

  const columns = [
    {
      key: 'name', label: PEOPLE.heads.name, width: 'minmax(0, 1.1fr)', primary: true,
      render: r => (
        <span className="apl-name">
          <Link to={adminPath.person(r.person_id)} className="ad-td-main">{r.full_name}</Link>
          <span className="ad-td-sub">{r.email}</span>
        </span>
      ),
    },
    {
      key: 'products', label: PEOPLE.heads.products, width: 'minmax(0, 1.5fr)',
      render: r => {
        const tags = listTags(r, productsById)
        if (!tags.length) return <span className="ad-td-quiet">{PEOPLE.noProducts}</span>
        return (
          <span className="apl-tags">
            {tags.map(t => (
              <span key={t.key} className={`ad-tag${t.outside ? ' apl-tag--outside' : ''}`}><Dot product={t.product} size={7} />{t.label}</span>
            ))}
          </span>
        )
      },
    },
    { key: 'status', label: PEOPLE.heads.status, width: '190px', render: r => <StatusPill status={r.status_key}>{listStatus(r, productsById)}</StatusPill> },
    { key: 'seen', label: PEOPLE.heads.seen, width: '110px', render: r => <span className="ad-td-quiet">{lastSeen(r.last_seen_at)}</span> },
    { key: 'joined', label: PEOPLE.heads.joined, width: '90px', render: r => <span className="ad-td-quiet">{boardDay(r.joined_at)}</span> },
  ]

  return (
    <>
      <PageHeader eyebrow={PEOPLE.eyebrow} title={PEOPLE.title} actions={actions} />
      {csvError && <Problem error={csvError} />}
      {oa && panel && !catalogue && (catalogueError ? <Problem error={catalogueError} /> : <Loading />)}
      {oa && panel === 'add' && catalogue && <AddPerson products={catalogue.catalogue} cohorts={catalogue.cohorts} onAdded={() => { setPanel(null); load() }} />}
      {batch && (
        <Flash actions={batch.canUndo ? <Button size="sm" onClick={undo} busy={undoing} busyLabel={BULK.undoBusy}>{BULK.undo}</Button> : null}>
          {batch.text}{batch.error ? ` ${batch.error}` : ''}
        </Flash>
      )}
      {oa && panel === 'bulk' && catalogue && <BulkGrant products={catalogue.catalogue} cohorts={catalogue.cohorts} onGranted={granted} />}

      <div className="apl-filters">
        <div className="apl-filter-row">
          <SearchBox label={PEOPLE.search} value={filters.search} onChange={v => set({ search: v })} />
          <div className="ad-chips apl-status-chips" role="group" aria-label={PEOPLE.statusLabel}>
            {STATUS_CHIPS.map(c => <Chip key={c.key || 'any'} tall pressed={filters.status === c.key} onClick={() => set({ status: c.key })}>{c.label}</Chip>)}
          </div>
        </div>
        <Chips label={PEOPLE.productLabel}>
          <Chip pressed={!filters.product} onClick={() => set({ product: '' })}>{PEOPLE.allProducts}</Chip>
          {productChips.map(p => <Chip key={p.id} product={p} pressed={filters.product === p.id} onClick={() => set({ product: p.id })}>{shortName(p)}</Chip>)}
        </Chips>
        {freeChips.length > 0 && (
          <Chips label={PEOPLE.freeLabel}>
            <Chip pressed={!filters.free} onClick={() => set({ free: '' })}>{PEOPLE.anyFree}</Chip>
            {freeChips.map(p => <Chip key={p.id} pressed={filters.free === p.id} onClick={() => set({ free: p.id })}>{freeChipName(p)}</Chip>)}
          </Chips>
        )}
      </div>

      {list.status === 'loading' && <Loading />}
      {list.status === 'error' && !rows.length && <Problem error={list.error} onRetry={load} />}
      {(list.status === 'ready' || list.status === 'reloading' || (list.status === 'error' && rows.length > 0)) && (
        <>
          <div aria-busy={list.status === 'reloading' || undefined}>
            <Table label={PEOPLE.title} rowKey={r => r.person_id} rows={rows} columns={columns} empty={PEOPLE.none} />
          </div>
          {list.status === 'error' && <Problem error={list.error} onRetry={showMore} />}
          <div className="apl-count">
            <span>{PEOPLE.count(rows.length, list.total)}</span>
            {rows.length < list.total && <Button size="sm" onClick={showMore} busy={more} busyLabel={PEOPLE.loadingMore}>{PEOPLE.more}</Button>}
          </div>
        </>
      )}

      {role === 'owner' && (
        <Card title={PEOPLE.spacesTitle} actions={<Button size="sm" onClick={() => setOpening(true)}>{SPACE.openTitle}</Button>}>
          <p className="apl-hint">{liveSpaceProducts(products).length > 1 ? PEOPLE.spacesLineMany : PEOPLE.spacesLine}</p>
        </Card>
      )}
      {opening && <OpenSpaceDialog onClose={() => setOpening(false)} onOpened={() => load()} />}
    </>
  )
}
